import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { gpsTracking } from '@/services/gpsTracking';
import { useToast } from '@/shared/toast';
import { useConfirm } from '@/shared/confirm';
import { notifyNativeSelectiveResponse } from '@/services/nativeBridge';
import { parseRespondResult, type SelectiveConflictBody } from '@vvf/shared';

/**
 * Hook condiviso per gestire POST /selectives/{id}/respond da qualsiasi
 * componente PWA (ActiveSelectiveItem, GlobalSelectiveAlert modal multi-sel).
 * Centralizza:
 *
 *  - acquisizione GPS quando si accetta
 *  - gestione 409 AlreadyActiveOnAnother con confirm di switch (rifiuta
 *    la vecchia + accetta la nuova)
 *  - toast e invalidate queries
 *
 * Il chiamante passa il `selectiveId` e ottiene una promise che resolve a
 * 'accepted' | 'rejected' | 'cancelled' (per distinguere il "no" del
 * confirm switch dal vero rifiuto). I componenti possono usare il valore
 * per aggiornare il loro state locale (es. myResponseLocal in ActiveSelectiveItem).
 */
export type RespondOutcome = 'accepted' | 'rejected' | 'cancelled' | 'error';

export function useSelectiveResponseHandler(): {
  /**
   * Accetta una selettiva. Acquisisce GPS, gestisce 409 con confirm.
   * Ritorna 'accepted' su success, 'cancelled' se l'utente annulla lo
   * switch, 'error' se la POST fallisce per altro motivo.
   */
  accept: (selectiveId: string, getGps: () => Promise<{ lat: number | null; lng: number | null }>) => Promise<RespondOutcome>;
  /** Rifiuta una selettiva. Nessun GPS, nessuna gestione 409. */
  reject: (selectiveId: string) => Promise<RespondOutcome>;
} {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const accept = useCallback(async (
    selectiveId: string,
    getGps: () => Promise<{ lat: number | null; lng: number | null }>,
  ): Promise<RespondOutcome> => {
    const coords = await getGps();
    try {
      await api.post(`/selectives/${selectiveId}/respond`, {
        response: 'Accepted', latitude: coords.lat, longitude: coords.lng,
      });
      onSuccess(selectiveId, 'Accepted');
      return 'accepted';
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: SelectiveConflictBody } };
      // A7.3: stessa interpretazione del 409 usata dal wrapper mobile (shared).
      const result = parseRespondResult(e?.response?.status ?? 0, e?.response?.data ?? null);
      if (result.kind !== 'conflict') {
        toast.show('Errore invio risposta', 'error');
        return 'error';
      }
      const otherId = result.activeSelectiveId;
      const otherType = result.activeSelectiveType;
      const otherZone = result.activeSelectiveZone;
      const ok = await confirm({
        title: '⚠️ Sei già attivo su un\'altra selettiva',
        message: `Sei attualmente attivo su "${otherType}"${otherZone ? ` in ${otherZone}` : ''}. Per accettare questa devi prima rifiutarla. Procedere?`,
        confirmLabel: `Rifiuta "${otherType}" e accetta questa`,
        cancelLabel: 'Annulla',
        variant: 'danger',
      });
      if (!ok) return 'cancelled';
      try {
        await api.post(`/selectives/${otherId}/respond`, { response: 'Rejected', latitude: null, longitude: null });
      } catch {
        toast.show('Errore nel rifiutare l\'altra selettiva', 'error');
        return 'error';
      }
      if (gpsTracking.getActive() === otherId) gpsTracking.stop();
      try {
        await api.post(`/selectives/${selectiveId}/respond`, {
          response: 'Accepted', latitude: coords.lat, longitude: coords.lng,
        });
      } catch {
        toast.show('Errore invio risposta', 'error');
        return 'error';
      }
      qc.invalidateQueries({ queryKey: ['selective.responses', otherId] });
      onSuccess(selectiveId, 'Accepted');
      return 'accepted';
    }
  }, [qc, toast, confirm]);

  const reject = useCallback(async (selectiveId: string): Promise<RespondOutcome> => {
    try {
      await api.post(`/selectives/${selectiveId}/respond`, {
        response: 'Rejected', latitude: null, longitude: null,
      });
      onSuccess(selectiveId, 'Rejected');
      return 'rejected';
    } catch {
      toast.show('Errore invio risposta', 'error');
      return 'error';
    }
  }, [toast]);

  function onSuccess(selectiveId: string, response: 'Accepted' | 'Rejected'): void {
    qc.invalidateQueries({ queryKey: ['selectives.active'] });
    qc.invalidateQueries({ queryKey: ['selective.responses', selectiveId] });
    // Notifica wrapper RN: rimuove la selettiva da pendingSelectives e
    // chiude il modal nativo se aperto. No-op in PWA pura (no wrapper).
    // Senza questo, se l'utente accettava da /selectives mentre il modal
    // rosso era ancora aperto, il modal restava su una selettiva gia' gestita.
    notifyNativeSelectiveResponse(selectiveId, response === 'Accepted');
    if (response === 'Accepted') {
      toast.show('Selettiva accettata! GPS attivo.', 'success');
      gpsTracking.start(selectiveId);
    } else {
      toast.show('Selettiva rifiutata', 'info');
      if (gpsTracking.getActive() === selectiveId) gpsTracking.stop();
    }
  }

  return { accept, reject };
}
